import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { ReactNode } from 'react'
import type { RootState } from '../store'
import { CONTACT, SERVICES, STORE } from '../config/landing'
import { SiteHeader } from '../components/SiteHeader'
import './Landing.css'

function AppleGlyph() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.4 12.8c0-2.1 1.7-3.1 1.8-3.2-1-1.4-2.5-1.6-3-1.7-1.3-.1-2.5.8-3.1.8-.7 0-1.7-.7-2.8-.7-1.4 0-2.8.9-3.5 2.2-1.5 2.6-.4 6.5 1.1 8.6.7 1 1.6 2.2 2.7 2.1 1.1 0 1.5-.7 2.8-.7s1.7.7 2.8.7c1.2 0 1.9-1 2.6-2 .8-1.2 1.1-2.3 1.1-2.4-.1 0-2.1-.8-2.1-3.7zm-2-6.2c.6-.7 1-1.7.9-2.7-0.9.1-1.9.6-2.5 1.3-.6.6-1.1 1.6-1 2.6 1 .1 1.9-.5 2.6-1.2z"
      />
    </svg>
  )
}

function PlayGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M3.6 2.2 13.4 12 3.6 21.8z" />
      <path fill="#FBBC04" d="M13.4 12 3.6 21.8l12.2-6.9z" />
      <path fill="#4285F4" d="M20.4 10.1 15.8 12.7 13.4 12l2.4-.7 4.6-2.6z" />
      <path fill="#34A853" d="M3.6 2.2 15.8 9.1 13.4 12z" />
    </svg>
  )
}

function StoreBadge({
  href,
  label,
  sub,
  icon,
  soon,
}: {
  href?: string
  label: string
  sub: string
  icon: ReactNode
  soon?: boolean
}) {
  const inner = (
    <>
      <span className="store-badge__icon">{icon}</span>
      <span className="store-badge__text">
        <small>{sub}</small>
        <strong>{label}</strong>
      </span>
    </>
  )
  if (soon || !href) {
    return (
      <span className="store-badge store-badge--soon" aria-disabled="true">
        {inner}
      </span>
    )
  }
  return (
    <a className="store-badge" href={href} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  )
}

function StoreButtons({ className = '' }: { className?: string }) {
  return (
    <div className={`store-row ${className}`.trim()}>
      <StoreBadge href={STORE.android} label="Google Play" sub="Get it on" icon={<PlayGlyph />} />
      <StoreBadge
        href={STORE.ios || undefined}
        label="App Store"
        sub={STORE.ios ? 'Download on the' : 'Coming soon on'}
        icon={<AppleGlyph />}
        soon={!STORE.ios}
      />
    </div>
  )
}

const SHOTS = {
  fleet: '/images/screen1.png',
  reports: '/images/screen2.png',
  details: '/images/screen3.png',
} as const

function PhoneShell({
  src,
  className = '',
}: {
  src: string
  className?: string
}) {
  return (
    <div className={`phone ${className}`.trim()}>
      <div className="phone__chin" aria-hidden="true" />
      <div className="phone__screen">
        <img src={src} alt="" className="phone__shot" draggable={false} />
      </div>
      <div className="phone__home" aria-hidden="true" />
    </div>
  )
}

const STEPS = [
  {
    title: 'Install & sign in',
    text: 'Download DriveOps, create your account, and use the same credentials on mobile or web.',
  },
  {
    title: 'Add your vehicles',
    text: 'Register plates, plans, and trackers so every asset shows up in Suivi with live status.',
  },
  {
    title: 'Track on the map',
    text: 'Follow positions in list or map view — ignition, speed, fuel, and battery in one glance.',
  },
  {
    title: 'Review & report',
    text: 'Replay trips by day, then pull distance and engine-time reports for the week your ops team needs.',
  },
] as const

const FEATURES = [
  {
    mark: '01',
    title: 'Live fleet picture',
    text: 'Positions and telemetry update as trackers move — dispatch and support share the same truth.',
  },
  {
    mark: '02',
    title: 'List & map views',
    text: 'Search vehicles, switch layouts, and open details without leaving the field workflow.',
  },
  {
    mark: '03',
    title: 'Trip history',
    text: 'Open any day, follow the polyline, and inspect points with timestamps and addresses.',
  },
  {
    mark: '04',
    title: 'Distance reports',
    text: 'Measure kilometres, engine hours, driving vs idle — computed from real histories.',
  },
  {
    mark: '05',
    title: 'Plans & payments',
    text: 'Onboard vehicles, manage subscriptions, and keep billing aligned with active assets.',
  },
  {
    mark: '06',
    title: 'Alerts & settings',
    text: 'Notification preferences, profile, and multi-language support for en / fr teams.',
  },
] as const

export function LandingPage() {
  const token = useSelector((s: RootState) => s.auth.accessToken)
  const signInTo = token ? '/app' : '/login'
  const signInLabel = token ? 'Continue' : 'Sign in'

  return (
    <div className="landing">
      <div className="landing__glow" aria-hidden="true" />

      <SiteHeader homeAnchors />

      <main className="landing__wrap">
        <section id="top" className="landing__hero" aria-labelledby="hero-title">
          <div className="landing__hero-copy">
            <span className="landing__eyebrow">Fleet · Telematics · Operations</span>
            <h1 id="hero-title">
              Keep every vehicle <em>visible</em> — live.
            </h1>
            <p className="landing__lead">
              DriveOps connects GPS trackers, maps, and your team so dispatch can see positions, replay trips, and pull
              distance reports from one place.
            </p>
            <StoreButtons />
            <p className="landing__note">Free to install · Sign in on web or mobile · Same account everywhere</p>
            <div className="landing__stats" aria-label="Highlights">
              <div>
                <strong>Live map</strong>
                <span>Fleet positions &amp; telemetry</span>
              </div>
              <div>
                <strong>History</strong>
                <span>Day-by-day trip replay</span>
              </div>
              <div>
                <strong>Reports</strong>
                <span>Distance &amp; engine time</span>
              </div>
            </div>
          </div>

          <div className="landing__phones" aria-hidden="true">
            <div className="landing__phone-glow" />
            <div className="landing__phone-wrap landing__phone-wrap--front">
              <PhoneShell src={SHOTS.fleet} />
            </div>
            <div className="landing__phone-wrap landing__phone-wrap--back">
              <PhoneShell src={SHOTS.reports} />
            </div>
          </div>
        </section>

        <section id="how" aria-labelledby="how-title">
          <div className="landing__section-head">
            <span className="landing__eyebrow">How it works</span>
            <h2 id="how-title">From install to daily ops in four steps</h2>
            <p>Everything your depot needs — track, replay, report — without spreadsheets or radio chase-downs.</p>
          </div>
          <div className="landing__steps">
            {STEPS.map((step, i) => (
              <article key={step.title} className="landing__step">
                <div className="landing__step-num">{i + 1}</div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="features" aria-labelledby="features-title">
          <div className="landing__section-head">
            <span className="landing__eyebrow">Built for fleets</span>
            <h2 id="features-title">Real product surfaces — not a brochure widget</h2>
            <p>The same flows your teams use in the DriveOps mobile app and web sign-in.</p>
          </div>
          <div className="landing__features">
            {FEATURES.map((f) => (
              <article key={f.mark} className="landing__feature">
                <span className="landing__feature-mark">{f.mark}</span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="services" aria-labelledby="services-title" className="landing__services-band">
          <div>
            <span className="landing__eyebrow">Services</span>
            <h2 id="services-title">What DriveOps delivers day to day</h2>
            <p>
              One platform for vehicles, trackers, plans, and people — structured around the entities operations already
              reason about.
            </p>
          </div>
          <ul className="landing__service-chips">
            {SERVICES.map((s) => (
              <li key={s.id}>
                <strong>{s.title}</strong>
                <span>{s.text}</span>
              </li>
            ))}
          </ul>
        </section>

        <section id="download" className="landing__cta-band" aria-labelledby="download-title">
          <div className="landing__cta-phones" aria-hidden="true">
            <PhoneShell className="phone--compact" src={SHOTS.details} />
          </div>
          <span className="landing__eyebrow landing__eyebrow--on-dark">Download DriveOps</span>
          <h2 id="download-title">Your fleet, one tap away.</h2>
          <p>Join on Android today. iOS is on the way. Prefer a browser? Sign in with the same account.</p>
          <StoreButtons className="store-row--center" />
          <Link to={signInTo} className="landing__cta-signin">
            {signInLabel} →
          </Link>
        </section>

        <section id="contact" aria-labelledby="contact-title">
          <div className="landing__section-head">
            <span className="landing__eyebrow">Contact</span>
            <h2 id="contact-title">Talk to the team behind DriveOps</h2>
            <p>
              Questions about trackers, plans, or rolling out a fleet? Reach {CONTACT.company} — we build and operate
              DriveOps.
            </p>
          </div>
          <div className="landing__contact">
            <a href={CONTACT.phoneHref}>
              <span>Phone</span>
              <strong>{CONTACT.phone}</strong>
            </a>
            <a href={CONTACT.emailHref}>
              <span>Email</span>
              <strong>{CONTACT.email}</strong>
            </a>
            <a href={CONTACT.mapsHref} target="_blank" rel="noopener noreferrer">
              <span>Headquarters</span>
              <strong>{CONTACT.address}</strong>
            </a>
          </div>
        </section>
      </main>

      <footer className="landing__footer">
        <div className="landing__wrap landing__footer-inner">
          <span className="landing__footer-brand">
            <img src="/logo.png" alt="" width={28} height={28} />
            © {new Date().getFullYear()} DriveOps · {CONTACT.company}
          </span>
          <div className="landing__footer-links">
            <a href="#how">How it works</a>
            <a href="#features">Features</a>
            <a href="#download">Install</a>
            <a href="#contact">Contact</a>
            <Link to={signInTo}>{signInLabel}</Link>
            <a href="http://driveops.omnivatis.com/privacy-policy" target="_blank" rel="noreferrer">
              Privacy
            </a>
            <a href="http://driveops.omnivatis.com/terms-of-use" target="_blank" rel="noreferrer">
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
